import React, { useState } from "react";
import client from "../lib/api";
import { Card, Button, Select } from "../components/ui";
import { CalendarClock, Download, Mail, ShieldCheck, FileText, Wallet, BarChart3, CheckCircle2, Loader2 } from "lucide-react";

function last12Months() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 12; i++) {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    out.push({ key: `${y}-${String(m).padStart(2, "0")}`, label: d.toLocaleString("en", { month: "long", year: "numeric" }) });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

async function download(url, filename) {
  const res = await client.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href; a.download = filename; a.click();
  URL.revokeObjectURL(href);
}

function LogCard({ icon: Icon, title, desc, children, testid }) {
  return (
    <Card className="space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2 font-head text-lg"><Icon size={18} className="text-[var(--primary)]" /> {title}</div>
      <p className="text-sm opacity-60">{desc}</p>
      {children}
    </Card>
  );
}

export default function MonthlyLogs() {
  const months = last12Months();
  const [dPeriod, setDPeriod] = useState(months[0].key);
  const [aPeriod, setAPeriod] = useState(months[0].key);
  const [dablPeriod, setDablPeriod] = useState("monthly");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");

  const run = async (id, fn) => {
    setBusy(id); setMsg("");
    try { const r = await fn(); if (r) setMsg(r); } catch { setMsg("Something went wrong — please try again."); }
    setBusy("");
  };

  const revenueRange = () => {
    const [y, m] = aPeriod.split("-").map(Number);
    const frm = `${aPeriod}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const to = `${aPeriod}-${String(lastDay).padStart(2, "0")}`;
    return { frm, to };
  };

  return (
    <div className="space-y-8 fadeup" data-testid="monthly-logs-page">
      <div>
        <h1 className="font-head text-4xl font-light flex items-center gap-2"><CalendarClock className="text-[var(--primary)]" /> Monthly Logs</h1>
        <p className="opacity-60">Every monthly record in one place. Nothing is auto-sent — download or email each log whenever you need it.</p>
      </div>
      {msg && <div className="rounded-md border border-[var(--primary)]/40 bg-[var(--primary)]/5 px-3 py-2 text-sm" data-testid="ml-msg">{msg}</div>}

      <div className="grid gap-5 lg:grid-cols-2">
        <LogCard icon={ShieldCheck} title="Delegation audit" testid="ml-delegation"
          desc="Grants, updates, extensions, admin ends and auto-expiries of delegated admin access for the selected month.">
          <Select label="Month" value={dPeriod} onChange={(e) => setDPeriod(e.target.value)} data-testid="ml-deleg-month">
            {months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </Select>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" disabled={busy === "dpdf"} onClick={() => run("dpdf", () => download(`/admin/delegation/audit.pdf?period=${dPeriod}`, `delegation_audit_${dPeriod}.pdf`))} data-testid="ml-deleg-pdf">
              {busy === "dpdf" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF
            </Button>
            <Button variant="ghost" disabled={busy === "dcsv"} onClick={() => run("dcsv", () => download(`/admin/delegation/audit.csv?period=${dPeriod}`, `delegation_audit_${dPeriod}.csv`))} data-testid="ml-deleg-csv">
              {busy === "dcsv" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} CSV
            </Button>
            <Button disabled={busy === "dmail"} onClick={() => run("dmail", async () => { const r = await client.post(`/admin/delegation/audit/email?period=${dPeriod}`); return `Delegation audit for ${dPeriod} emailed to ${r.data.emailed_to}.`; })} data-testid="ml-deleg-email">
              {busy === "dmail" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email to me
            </Button>
          </div>
        </LogCard>

        <LogCard icon={FileText} title="Account monthly log" testid="ml-account"
          desc="Your account's security, activity and licensing snapshot for the month (retained 12 months).">
          <Select label="Month" value={aPeriod} onChange={(e) => setAPeriod(e.target.value)} data-testid="ml-acct-month">
            {months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </Select>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" disabled={busy === "apdf"} onClick={() => run("apdf", () => download(`/monthly-logs/${aPeriod}/pdf`, `monthly-log-${aPeriod}.pdf`))} data-testid="ml-acct-pdf">
              {busy === "apdf" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF
            </Button>
            <Button disabled={busy === "amail"} onClick={() => run("amail", async () => { const r = await client.post(`/monthly-logs/${aPeriod}/email`); return `Account log for ${aPeriod} emailed to ${r.data.emailed_to}.`; })} data-testid="ml-acct-email">
              {busy === "amail" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email to me
            </Button>
          </div>
        </LogCard>

        <LogCard icon={Wallet} title="DABL financials" testid="ml-dabl"
          desc="Deferred / Accrued Benefit Ledger figures for the chosen period.">
          <Select label="Period" value={dablPeriod} onChange={(e) => setDablPeriod(e.target.value)} data-testid="ml-dabl-period">
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </Select>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" disabled={busy === "dablcsv"} onClick={() => run("dablcsv", () => download(`/dabl/export?period=${dablPeriod}&fmt=csv`, `dabl_${dablPeriod}.csv`))} data-testid="ml-dabl-csv">
              {busy === "dablcsv" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} CSV
            </Button>
          </div>
        </LogCard>

        <LogCard icon={BarChart3} title="Revenue report" testid="ml-revenue"
          desc="Activations, paid unlocks and revenue for the selected month.">
          <Select label="Month" value={aPeriod} onChange={(e) => setAPeriod(e.target.value)} data-testid="ml-rev-month">
            {months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </Select>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" disabled={busy === "revcsv"} onClick={() => run("revcsv", () => { const { frm, to } = revenueRange(); return download(`/admin/audit-report.csv?frm=${frm}&to=${to}`, `revenue_${aPeriod}.csv`); })} data-testid="ml-rev-csv">
              {busy === "revcsv" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} CSV
            </Button>
          </div>
        </LogCard>
      </div>

      <div className="flex items-center gap-2 text-xs opacity-50" data-testid="ml-footnote">
        <CheckCircle2 size={13} /> Logs are generated on demand and await your download or email — nothing is sent automatically.
      </div>
    </div>
  );
}
